import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import FilterSearch from "../../components/Filtering/FilterSearch";
import SearchButton from "../../components/buttons/SearchButton";
import FilterRightBox from "../../components/Filtering/FilterRightBoxOffering";
import BreadCrumb from "../../components/HeaderComponents/BreadCrum";
import { useWorkerApiHelper } from "../../hooks/useWorkerApiHelper";
import { Spinner } from "../../components/Filtering/Reusable/Spinner";
import { useTableControls } from "../../hooks/useTableControls";
import ParticipantCard from "../../components/ParticipantCard/ParticipantCard";
import { buildParticipantSearchText } from "../../utils/participants";
import { useParticipantDisplayVocabularies } from "../../hooks/participants/useParticipantDisplayVocabularies";
import "./ParticipantDiscovery.css";
import { useGetParticipants } from "../../hooks/participants/useGetParticipants";

function getResultsLabel(t, count) {
  return count === 1
    ? t("participant.discovery_result")
    : t("participant.discovery_results");
}

function ParticipantDiscovery() {
  const { callApiWithWorker } = useWorkerApiHelper();
  const [data, setData] = useState([]);
  const { t, i18n } = useTranslation();
  const {
    tableData,
    searchTerm,
    pendingSearchTerm,
    open,
    pagination,
    setTableData,
    handleSearch,
    handleClearSearch,
    handleSearchSubmit,
    handleSearchKeyDown,
    handleClose,
    handleApplyFilters,
  } = useTableControls([]);

  const {
    data: participantsResponse,
    isLoading,
    isFetching,
  } = useGetParticipants({
    callApiWithWorker,
    lang: i18n.language,
    pageIndex: pagination.pageIndex,
    pageSize: pagination.pageSize,
    status: "active",
  });
  const participantVocabularies = useParticipantDisplayVocabularies({
    callApiWithWorker,
    lang: i18n.language,
  });

  const loading = isLoading || isFetching;

  useEffect(() => {
    const participants = participantsResponse?.content ?? [];
    setTableData(participants);
  }, [participantsResponse, setTableData]);

  useEffect(() => {
    setData(tableData);
  }, [tableData]);

  const filteredData = data.filter((participant) =>
    buildParticipantSearchText(participant, i18n.language)
      .toLowerCase()
      .includes(searchTerm.toLowerCase()),
  );

  return (
    <section className="pb4">
      <div className="w100 flexbread">
        <div className="divbreadcrum w90">
          <BreadCrumb />
        </div>
      </div>
      <div className="participant-conatiner containerflex">
        <FilterRightBox
          open={open}
          onClose={handleClose}
          onApplyFilters={handleApplyFilters}
        />
        <div className="w90 bgwhite mtb2">
          <div className="plr3 ptb">
            <div className="participant-discovery-heading">
              <h2 className="tittlecatalog">{t("participant.discovery")}</h2>
              <p className="participant-discovery-subtitle">
                {t("participant.discovery_subtitle")}
              </p>
            </div>
            <div key={t.language} className="participant-discovery-search">
              <div className="filteractions">
                <FilterSearch
                  value={pendingSearchTerm}
                  onChange={handleSearch}
                  onClear={handleClearSearch}
                  onKeyDown={handleSearchKeyDown}
                />
                <div className="plr2">
                  <SearchButton onClick={handleSearchSubmit} />
                </div>
              </div>
            </div>
            <div className="participant-discovery-summary">
              {(() => {
                const resultCount =
                  participantsResponse?.totalElements ?? filteredData.length;
                return (
                  <span className="participant-summary-pill">
                    {resultCount} {getResultsLabel(t, resultCount)}
                  </span>
                );
              })()}
            </div>
            <div className="grid">
              {filteredData.map((p) => (
                <ParticipantCard
                  key={
                    p.id ??
                    p.participant_id ??
                    p.connector?.[0]?.identifier ??
                    p.identifier?.[0]?.notation
                  }
                  participant={p}
                  countryOptions={participantVocabularies.countries}
                  provinceOptions={participantVocabularies.provinces}
                  classificationOptions={
                    participantVocabularies.organizationType
                  }
                  purposeOptions={participantVocabularies.governmentFunctions}
                />
              ))}
            </div>
            {filteredData.length === 0 && !loading && (
              <p style={{ padding: "20px", textAlign: "center" }}>
                {t("datatable.noData")}
              </p>
            )}
          </div>
        </div>
      </div>
      <div>
        {loading && (
          <div className="loading">
            <Spinner size="large" color="var(--main-color)" />
          </div>
        )}
      </div>
    </section>
  );
}

export default ParticipantDiscovery;
